<script setup lang="ts">
import type { IContentFlagRequest } from "@/api/flagInappropriateContent"
import type { Comment, GuestbookMessage, Media } from "@/models"
import { useQueryClient } from "@tanstack/vue-query"
import { toTypedSchema } from "@vee-validate/zod"
import { useForm } from "vee-validate"
import { computed } from "vue"
import z from "zod"
import { FlagReasonType, useFlagInappropriateContent } from "@/api/flagInappropriateContent"
import { MediaTile } from "@/components"
import { BaseModal, Button, Icon, Select, Textarea } from "@/components/ui"
import { useToast } from "@/components/ui/ToastReka/useToast"
import { useModal, useTranslation } from "@/composables"
import { FlagContentModalKey } from "@/symbols"
import { ContentType } from "@/types"

type ReasonTypeString = "Inappropriate" | "Offensive" | "Spam" | "Violence" | "Harassment" | "Other"

type FlaggedContent
  = | { type: "media", content: Media }
    | { type: "comment", content: Comment }
    | { type: "guestbookMessage", content: GuestbookMessage }

interface GuestDetailsModalProps {
  eventVaultId?: number
  content?: FlaggedContent
}

const props = defineProps<GuestDetailsModalProps>()

const emit = defineEmits<{
  (e: "success"): void
  (e: "error"): void
  (e: "cancel"): void
}>()

defineExpose({
  clearFormValues,
})

const { t } = useTranslation()
const toast = useToast()

const { toggle: toggleModal } = useModal(FlagContentModalKey)

const validationSchema = toTypedSchema(
  z.object({
    reasonType: z.enum(["Inappropriate", "Offensive", "Spam", "Violence", "Harassment", "Other"]),
    reasonDetails: z.string().max(500, t("flag_content_modal.form.reason_details_max")).optional(),
  }),
)

const { handleSubmit, resetForm } = useForm({
  validationSchema,
  initialValues: {
    reasonType: "Inappropriate",
  },
})

const eventVaultId = computed(() => props.eventVaultId)

const { mutate: flagContent, isPending: isSubmitting } = useFlagInappropriateContent()

const queryClient = useQueryClient()

const computedContentType = computed(() => {
  const type = props.content?.type

  if (type === "media") {
    return props.content?.content.type === "pic" ? ContentType.Photo : ContentType.Video
  }

  if (type === "comment") {
    return ContentType.Comment
  }

  if (type === "guestbookMessage") {
    // if its a video guestbook message, still send the type as Video
    return props.content?.content.videoUrl ? ContentType.Video : ContentType.Message
  }

  return undefined
})

const onSubmit = handleSubmit(async (values) => {
  if (!props.content || computedContentType.value === undefined || !eventVaultId.value) {
    toast.error()
    emit("error")
    return
  }

  const reasonTypeMap: Record<ReasonTypeString, FlagReasonType> = {
    Inappropriate: FlagReasonType.Inappropriate,
    Offensive: FlagReasonType.Offensive,
    Spam: FlagReasonType.Spam,
    Violence: FlagReasonType.Violence,
    Harassment: FlagReasonType.Harassment,
    Other: FlagReasonType.Other,
  }

  const content: IContentFlagRequest = {
    contentId: props.content.content.id ?? 0,
    contentType: computedContentType.value,
    reason: reasonTypeMap[values.reasonType],
    reasonDetails: values.reasonDetails,
  }

  flagContent({ eventVaultId: eventVaultId.value, content }, {
    onSuccess: async () => {
      props.content?.type === "media" && await queryClient.invalidateQueries({ queryKey: ["listMedia", eventVaultId] })
      props.content?.type === "comment" && await queryClient.invalidateQueries({ queryKey: ["listComments", eventVaultId, props.content.content] })
      props.content?.type === "guestbookMessage" && await queryClient.invalidateQueries({ queryKey: ["listMessages", eventVaultId] })

      toast.success({ description: t("flag_content_modal.form.success_message") })
      emit("success")
      clearFormValues()
      toggleModal()
    },
    onError: () => {
      toast.error()
      emit("error")
    },
  })
})

const flagReasonOptions = [
  { label: t("flag_content_modal.reason_options.inappropriate"), value: "Inappropriate" },
  { label: t("flag_content_modal.reason_options.offensive"), value: "Offensive" },
  { label: t("flag_content_modal.reason_options.spam"), value: "Spam" },
  { label: t("flag_content_modal.reason_options.violence"), value: "Violence" },
  { label: t("flag_content_modal.reason_options.harassment"), value: "Harassment" },
  { label: t("flag_content_modal.reason_options.other"), value: "Other" },
]

function clearFormValues() {
  resetForm({ values: {
    reasonType: "Inappropriate",
    reasonDetails: undefined,
  } })
}

function handleCancel() {
  emit("cancel")
  clearFormValues()
  toggleModal()
}
</script>

<template>
  <BaseModal :name="FlagContentModalKey" data-test="flag-content-modal">
    <div class="bg-white rounded-lg overflow-hidden max-h-[80vh] flex flex-col">
      <div class="flex-shrink-0 bg-white p-6 pb-4 border-b border-gray-200">
        <div class="flex items-start justify-between">
          <div class="flex items-center gap-2">
            <Icon icon="Flag" />
            <h1 class="font-comfortaa font-semibold text-xl">
              {{ t("flag_content_modal.title") }}
            </h1>
          </div>
          <Button variant="flat" prefix-icon="Close" :aria-label="t('flag_content_modal.close_modal')" class="p-0!" @click="handleCancel" />
        </div>
        <p class="text-sm font-montserrat text-label mt-1">
          {{ t("flag_content_modal.description") }}
        </p>
      </div>

      <div class="flex-1 overflow-y-auto p-6 flex flex-col gap-6 font-montserrat">
        <MediaTile v-if="content?.type === 'media'" :media="content.content" class="bg-gray-50" data-test="media-tile" />

        <div v-if="content?.type === 'comment'" class="flex items-start gap-4 bg-gray-50 p-4 rounded-lg border" data-test="comment-display">
          <Icon icon="ChatText" size="lg" class="shrink-0" />
          <div>
            <span class="font-semibold">{{ t("flag_content_modal.comment_label") }}</span> "{{ content.content.content }}" <br> <span class="font-semibold">{{ t("flag_content_modal.from_label") }}</span> {{ content.content.createdBy }}
          </div>
        </div>

        <div v-if="content?.type === 'guestbookMessage'" class="flex items-start gap-4 bg-gray-50 p-4 rounded-lg border" data-test="guestbook-message-display">
          <Icon icon="BookOpen" size="lg" class="shrink-0" />
          <div>
            <span class="font-semibold">{{ t("flag_content_modal.message_label") }}</span> "{{ content.content.message }}" <br> <span class="font-semibold">{{ t("flag_content_modal.from_label") }}</span> {{ content.content.fromName }}
          </div>
        </div>

        <form data-test="flag-form">
          <div class="flex flex-col gap-4">
            <Select name="reasonType" :label="t('flag_content_modal.form.reason_for_flagging_label')" :items="flagReasonOptions" :disabled="isSubmitting" data-test="reason-select" />
            <Textarea name="reasonDetails" :label="t('flag_content_modal.form.tell_us_more')" :disabled="isSubmitting" data-test="reason-details-textarea" />
          </div>
        </form>
      </div>

      <div class="flex-shrink-0 bg-white border-t border-gray-200 p-6 pt-4">
        <div class="flex gap-3 justify-end">
          <Button variant="danger" data-test="cancel-button" :disabled="isSubmitting" @click="handleCancel">
            {{ t("cancel") }}
          </Button>
          <Button variant="primary" data-test="confirm-button" :disabled="isSubmitting" :is-loading="isSubmitting" @click="onSubmit">
            {{ isSubmitting ? t("flag_content_modal.form.flagging") : t("flag_content_modal.form.flag_btn") }}
            <template #suffixIcon>
              <Icon v-if="isSubmitting" icon="Loading" />
            </template>
          </Button>
        </div>
      </div>
    </div>
  </BaseModal>
</template>
